import { Portal } from '@ark-ui/react'
import { FloatingPortalBoundary } from '@membranehq/react'
import { Menu } from 'components/Menu'
import { cn } from 'lib/utils'
import { forwardRef, PropsWithChildren, ReactNode, useState } from 'react'
import { TbChevronLeft, TbX } from 'react-icons/tb'
import { NavLink } from 'react-router-dom'
import { cx, sva } from 'styled-system/css'
import { Button } from 'ui-kit/button'
import { IconButton } from 'ui-kit/icon-button'
import { Input, InputProps } from 'ui-kit/input'
import * as UiKitPopup from 'ui-kit/styled/popup'

import { DebouncedInput, DebouncedInputProps } from '../DebouncedInput'
import { Tooltip } from '../Tooltip'
import {
  PopupWindowProps,
  PopupHeaderProps,
  PopupContentProps,
  PopupFooterProps,
  PopupTitleProps,
  TitleBackButtonProps,
  TitleBackButtonLinkProps,
} from './types'

const popupRecipe = sva({
  className: 'consolePopup',
  slots: ['headerInput', 'titleBackButton'],
  base: {
    headerInput: {
      zIndex: 'base',
      height: '12',
      width: 'auto',
      marginInline: '-5',
      paddingInlineStart: '5',
      paddingInlineEnd: '12',
      textStyle: 'lg',
      borderEndStartRadius: 'none',
      borderEndEndRadius: 'none',
      transitionProperty: 'opacity',
      transitionDuration: 'fast',
      transitionTimingFunction: 'ease-in-out',
      // _placeholder doesn't work here (probably because of legacy tailwind reset styles),
      // so we need to use _empty and opacity to achieve the same effect
      _empty: {
        opacity: 0.8,
        _hover: {
          opacity: 1,
        },
        _focusWithin: {
          opacity: 1,
        },
      },
      _hover: {
        backgroundColor: 'transparent',
      },
      _focusWithin: {
        backgroundColor: 'transparent',
      },
    },
    titleBackButton: {
      alignItems: 'center',
      fontSize: 'sm',
      paddingInlineStart: '1',
      paddingInlineEnd: '2',
      marginInlineStart: '-2',
      marginBlock: '-3',
      color: 'fg.muted',
      _hover: {
        color: 'fg.default',
      },
    },
  },
})

const popupClassnames = popupRecipe()

type PointerDownOutsideEvent = Parameters<NonNullable<UiKitPopup.RootProps['onPointerDownOutside']>>[0]
function handlePointerDownOutside(event: PointerDownOutsideEvent) {
  const target = event.detail.originalEvent.target as HTMLElement | null
  // Do not close popup when user clicks on toast or inside another dialog (nested popups)
  if (target?.closest('[data-scope="toast"], [data-scope="dialog"]')) {
    event.preventDefault()
    event.stopImmediatePropagation()
    event.stopPropagation()
  }
}

function Root({ children, ...props }: UiKitPopup.RootProps) {
  return (
    <UiKitPopup.Root lazyMount unmountOnExit onPointerDownOutside={handlePointerDownOutside} {...props}>
      {children}
    </UiKitPopup.Root>
  )
}

function RootProvider({ children, ...props }: UiKitPopup.RootProviderProps) {
  return (
    <UiKitPopup.RootProvider lazyMount unmountOnExit {...props}>
      {children}
    </UiKitPopup.RootProvider>
  )
}

const Window = forwardRef<HTMLDivElement, PopupWindowProps>(
  ({ children, usePortal, hideCloseButton, zIndex, ...props }, ref) => {
    const [hasAnimated, setHasAnimated] = useState(false)

    const handleAnimationEnd = (e: React.AnimationEvent) => {
      if (e.target === e.currentTarget) {
        setHasAnimated(true)
      }
    }

    const style = zIndex ? { zIndex } : undefined

    return (
      <Portal disabled={!usePortal}>
        <UiKitPopup.Backdrop style={style} />
        <UiKitPopup.Positioner style={style}>
          <FloatingPortalBoundary>
            <Menu.PortalBoundary>
              <UiKitPopup.Window
                ref={ref}
                data-has-animated={hasAnimated || undefined}
                onAnimationEnd={handleAnimationEnd}
                {...props}
              >
                {children}
                {!hideCloseButton && (
                  <UiKitPopup.CloseTrigger asChild className='absolute top-2.5 right-1.5 z-10'>
                    <IconButton aria-label='Close Dialog' variant='ghost' size='dense'>
                      <TbX />
                    </IconButton>
                  </UiKitPopup.CloseTrigger>
                )}
              </UiKitPopup.Window>
            </Menu.PortalBoundary>
          </FloatingPortalBoundary>
        </UiKitPopup.Positioner>
      </Portal>
    )
  },
)
Window.displayName = 'PopupWindow'

function HeaderRoot({ children, className, ...props }: PopupHeaderProps) {
  return (
    <UiKitPopup.Header
      className={cn(
        '[grid-area:header] flex flex-col gap-1 px-5 py-3 border-b border-muted',
        'has-[+[data-part=close-trigger]]:border-b-0',
        'has-[>.consolePopup--headerInput:first-child]:pt-0',
        'has-[>.consolePopup--headerInput:last-child]:pb-0',
        className,
      )}
      {...props}
    >
      {children}
    </UiKitPopup.Header>
  )
}

function Content({ children, className, ...props }: PopupContentProps) {
  return (
    <UiKitPopup.Content className={cn('[grid-area:content] overflow-y-auto p-4', className)} {...props}>
      {children}
    </UiKitPopup.Content>
  )
}

function Footer({ children, className, ...props }: PopupFooterProps) {
  return (
    <UiKitPopup.Footer
      className={cn(
        '[grid-area:footer] flex justify-end items-center gap-4 px-4 pb-2 pt-2.5 bg-zinc-50/50 border-t border-muted',
        className,
      )}
      {...props}
    >
      {children}
    </UiKitPopup.Footer>
  )
}

function TitleRoot({
  children,
  leftSlot,
  rightSlot,
  className,
  ...props
}: PopupTitleProps & { leftSlot?: ReactNode; rightSlot?: ReactNode }) {
  if (!children && !leftSlot && !rightSlot) {
    return null
  }

  return (
    <div className='flex items-center min-h-4 pe-8'>
      <SlotWrapper>{leftSlot}</SlotWrapper>
      {children && (
        <UiKitPopup.Title className={cn('grow m-0 p-0', className)} {...props}>
          {children}
        </UiKitPopup.Title>
      )}
      <SlotWrapper>{rightSlot}</SlotWrapper>
    </div>
  )
}

function SlotWrapper({ children }: PropsWithChildren) {
  if (!children) {
    return null
  }
  return children
}

const HeaderInput = forwardRef<HTMLInputElement, DebouncedInputProps>(({ className, ...props }, ref) => {
  return (
    <DebouncedInput
      variant='ghost'
      size='lg'
      className={cx('consolePopup--headerInput', popupClassnames.headerInput, className)}
      {...props}
      ref={ref}
    />
  )
})

const HeaderTriggerInput = forwardRef<HTMLInputElement, Omit<InputProps, 'onChange' | 'value'>>(
  ({ className, ...props }, ref) => {
    return (
      <Input
        variant='ghost'
        size='lg'
        className={cx(popupClassnames.headerInput, className)}
        {...props}
        ref={ref}
        value=''
        onChange={() => {}}
      />
    )
  },
)

function TitleBackButton({ children, ...props }: TitleBackButtonLinkProps | TitleBackButtonProps) {
  if (isTitleBackButtonLink(props)) {
    const { to, ...rest } = props
    return (
      <NavLink to={to} className='flex'>
        <TitleBackButton {...rest}>{children}</TitleBackButton>
      </NavLink>
    )
  }

  const { tooltip, className, ...rest } = props

  const button = (
    <Button variant='ghost' size='dense' className={cn('flex', popupClassnames.titleBackButton, className)} {...rest}>
      <TbChevronLeft className='size-5' aria-hidden />
      {children}
    </Button>
  )

  if (tooltip) {
    return <Tooltip tooltip={tooltip}>{button}</Tooltip>
  }

  return button
}

function isTitleBackButtonLink(
  props: TitleBackButtonLinkProps | TitleBackButtonProps,
): props is TitleBackButtonLinkProps {
  return (props as TitleBackButtonLinkProps).to !== undefined
}

const Header = Object.assign(HeaderRoot, {
  Input: HeaderInput,
  TriggerInput: HeaderTriggerInput,
})

const Title = Object.assign(TitleRoot, {
  BackButton: TitleBackButton,
})

const Popup = {
  Root,
  RootProvider,
  Window,
  Header,
  Content,
  Footer,
  Title,
  Trigger: UiKitPopup.Trigger,
  CloseTrigger: UiKitPopup.CloseTrigger,
  Description: UiKitPopup.Description,
  Context: UiKitPopup.Context,
}
export { Popup }
